import { useState } from 'react';
import { router } from '@inertiajs/react';

const priorityBadgeColors = {
    low: 'bg-info-subtle text-info',
    medium: 'bg-warning-subtle text-warning',
    high: 'bg-danger-subtle text-danger',
};

const statusBadgeColors = {
    pending: 'bg-warning-subtle text-warning',
    in_progress: 'bg-primary-subtle text-primary',
    completed: 'bg-success-subtle text-success',
    cancelled: 'bg-secondary-subtle text-secondary',
};

const typeIcons = {
    call: 'bi-telephone',
    meeting: 'bi-calendar-event',
    task: 'bi-list-task',
    email: 'bi-envelope',
    reminder: 'bi-bell',
};

const typeColors = {
    call: '#10b981',
    meeting: '#8b5cf6',
    task: '#3b82f6',
    email: '#6366f1',
    reminder: '#f59e0b',
};

export default function TaskCard({
    task,
    queryId,
    onEdit,
    onComplete,
    selectable = false,
    selected = false,
    onSelect,
}) {
    const [loading, setLoading] = useState(false);

    const formatDate = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
        });
    };

    const formatTime = (time) => {
        if (!time) return null;
        return time.substring(0, 5);
    };

    const isOverdue = task.due_date && task.status !== 'completed' && task.status !== 'cancelled' &&
        new Date(task.due_date) < new Date(new Date().setHours(0, 0, 0, 0));

    const isDueToday = task.due_date &&
        new Date(task.due_date).toDateString() === new Date().toDateString();

    const handleComplete = () => {
        if (loading) return;
        setLoading(true);
        router.patch(`/queries/${queryId}/tasks/${task.id}/complete`, {}, {
            preserveScroll: true,
            onFinish: () => setLoading(false),
        });
    };

    return (
        <div className={`task-card ${task.status === 'completed' ? 'completed' : ''} ${isOverdue ? 'overdue' : ''}`}>
            <div className="task-card-header">
                {selectable && (
                    <div className="task-checkbox">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected}
                            onChange={() => onSelect?.(task.id)}
                        />
                    </div>
                )}

                <div
                    className="task-type-icon"
                    style={{ backgroundColor: `${typeColors[task.type]}20`, color: typeColors[task.type] }}
                >
                    <i className={`bi ${typeIcons[task.type]}`}></i>
                </div>

                <div className="task-content">
                    <div className="task-subject">{task.subject}</div>
                    {task.description && (
                        <div className="task-description">{task.description}</div>
                    )}
                </div>

                <div className="task-meta">
                    <span className={`badge ${priorityBadgeColors[task.priority]}`}>
                        {task.priority === 'high' && <i className="bi bi-exclamation-triangle me-1"></i>}
                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                </div>
            </div>

            <div className="task-card-footer">
                <div className="task-info">
                    {task.due_date && (
                        <span className={`task-due ${isOverdue ? 'text-danger' : isDueToday ? 'text-warning' : ''}`}>
                            <i className="bi bi-calendar3 me-1"></i>
                            {formatDate(task.due_date)}
                            {task.due_time && ` ${formatTime(task.due_time)}`}
                            {isOverdue && ' (Overdue)'}
                            {isDueToday && !isOverdue && ' (Today)'}
                        </span>
                    )}

                    {task.assigned_to_user && (
                        <span className="task-assignee">
                            <i className="bi bi-person me-1"></i>
                            {task.assigned_to_user.name}
                        </span>
                    )}

                    {task.is_dsr && (
                        <span className="badge bg-primary-subtle text-primary">
                            <i className="bi bi-file-text me-1"></i>
                            DSR
                        </span>
                    )}
                </div>

                <div className="task-actions">
                    <span className={`badge ${statusBadgeColors[task.status]}`}>
                        {task.status.replace('_', ' ').charAt(0).toUpperCase() + task.status.replace('_', ' ').slice(1)}
                    </span>

                    {task.status !== 'completed' && task.status !== 'cancelled' && (
                        <button
                            className="btn btn-sm btn-outline-success"
                            onClick={handleComplete}
                            disabled={loading}
                            title="Mark Complete"
                        >
                            <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-check-lg'}`}></i>
                        </button>
                    )}

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => onEdit?.(task)}
                        title="Edit Task"
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                </div>
            </div>

            {task.status === 'completed' && task.outcome && (
                <div className="task-outcome">
                    <strong>Outcome:</strong> {task.outcome}
                </div>
            )}
        </div>
    );
}
